/**
 * personPreviewStore — the person the Studio is previewed as (CDEV-2234,
 * MEMFAB-PERSON-PREVIEW-ADDENDUM).
 *
 * While a viewer is set, the GraphQL client sends X-Fabric-Preview-As with
 * every request, refuses every mutation and subscription itself, and the
 * QueryProvider serves a separate TanStack cache, discarded on exit, so the
 * previewed view and the caller's own never share cached data (PP-7). The
 * server decides whether the preview is allowed and what it shows; this only
 * remembers what was asked for. Nothing is persisted: a reload exits.
 *
 * A role preview (rolePreviewStore) and a person preview do not combine:
 * entering one clears the other.
 */

import { create } from 'zustand';
import { useRolePreviewStore } from '@/stores/rolePreviewStore';

/** The request header that names the previewee (MEMFAB-PERSON-PREVIEW-ADDENDUM PP-1). */
export const PREVIEW_AS_HEADER = 'X-Fabric-Preview-As';

export interface PreviewedPerson {
  /** The previewee's full principal IRI. */
  iri: string;
  /** A display name, when the picker knew one; the server's name wins once read. */
  name: string | null;
}

interface PersonPreviewState {
  viewer: PreviewedPerson | null;
  /** When the preview began (ms since epoch); null when none. */
  enteredAt: number | null;
  startPersonPreview: (iri: string, name?: string | null) => void;
  exitPersonPreview: () => void;
}

/** Whether s is a full principal IRI the header can carry (https://…, no spaces). */
export function isFullPrincipalIri(s: string): boolean {
  return /^https?:\/\/[^\s,]+$/.test(s.trim());
}

/** The security namespace a compact sec: IRI or a bare principal-… id expands into. */
export const SECURITY_NS = 'https://ixfabric.ai/fabric/security/';

/**
 * The full principal IRI s names, or null: a full https://… IRI as is, the
 * compact sec:principal-… form a principal's own page shows, or the bare
 * principal-… id, expanded as the server expands them.
 */
export function normalizePrincipalIri(s: string): string | null {
  const t = s.trim();
  if (isFullPrincipalIri(t)) return t;
  if (/^sec:[^\s,]+$/.test(t)) return SECURITY_NS + t.slice('sec:'.length);
  if (/^principal-[^\s,]+$/.test(t)) return SECURITY_NS + t;
  return null;
}

/** Whether s looks like an attempt at an IRI (so a failure to read it is worth saying). */
export function looksLikeIri(s: string): boolean {
  return /^(https?:|sec:|principal-)/i.test(s.trim());
}

export const usePersonPreviewStore = create<PersonPreviewState>((set) => ({
  viewer: null,
  enteredAt: null,
  startPersonPreview: (iri, name = null) => {
    const trimmed = normalizePrincipalIri(iri);
    if (!trimmed) return;
    useRolePreviewStore.getState().clearPreviewRole();
    set({ viewer: { iri: trimmed, name: name?.trim() || null }, enteredAt: Date.now() });
  },
  exitPersonPreview: () => set({ viewer: null, enteredAt: null }),
}));

/** The previewee's IRI while previewing, else null (read at request time by the client). */
export function currentPreviewAs(): string | null {
  return usePersonPreviewStore.getState().viewer?.iri ?? null;
}

/** True while a person is previewed: write controls hide (PP-4). */
export function usePreviewingPerson(): boolean {
  return usePersonPreviewStore((s) => s.viewer !== null);
}

/** The last path segment of an IRI, as a fallback name. */
export function iriTail(iri: string): string {
  const parts = iri.split(/[/#:]/).filter(Boolean);
  return parts[parts.length - 1] ?? iri;
}
